import {
  useSessionId,
  useEppoClient,
  useEppoDefaultSubjectAttributes,
} from '@moonpig/web-core-utils'
import { useMemo } from 'react'
import { logger } from '@moonpig/web-core-monitoring'
import { EppoClient } from '@eppo/js-client-sdk-common'
import { useEppoExperimentOverrides } from '@moonpig/web-core-utils/src/eppoContext/EppoContext'
import { DefaultSubjectAttributes } from '../../shared/DefaultSubjectAttributes'

type AssignmentFunction<ReturnType> = (args: {
  client: EppoClient
  defaultAttributes: DefaultSubjectAttributes
  sessionId: string
  override: string | undefined
}) => ReturnType

export const useExperiment = <ReturnType>(
  experimentKey: string,
  fallback: ReturnType,
  assignmentCallback: AssignmentFunction<ReturnType>,
) => {
  const overrides = useEppoExperimentOverrides()
  const override = overrides?.[experimentKey]

  const { sessionId } = useSessionId()
  const eppoClient = useEppoClient()
  const eppoDefaultSubjectAttributes = useEppoDefaultSubjectAttributes()

  const assignedVariation = useMemo(() => {
    if (!eppoClient) {
      logger.fixToday('useEppoExperiment: Client is not defined')
      return fallback
    }
    if (!eppoDefaultSubjectAttributes) {
      logger.fixToday(
        'useEppoExperiment: Default subject attributes are not defined',
      )
      return fallback
    }

    if (!sessionId) return fallback

    return assignmentCallback({
      client: eppoClient,
      defaultAttributes: eppoDefaultSubjectAttributes,
      sessionId,
      override,
    })
  }, [
    assignmentCallback,
    eppoClient,
    eppoDefaultSubjectAttributes,
    fallback,
    override,
    sessionId,
  ])

  return assignedVariation
}
